/* eslint-disable indent */
import React, { useEffect, useState } from 'react';
import { PagePath } from '../../../navigation/navigation.enums';
import { PageHeader } from '@keplerco/core';
import { useAppActions, useAppState } from '../../../overmind';
import { CompanySkillListItemResponse } from '../../../models/overmind/company-entity';
import { Stepper } from '../../../components/general/stepper/stepper';
import { PeopleStepLayout } from './people-step/people-step.layout';
import { AssessmentType } from '../../../enums/assessment-type';
import { PageLoaderLayer } from '../../../components/general/loading-state/loader-layers/page-loader-layer/page-loader-layer';
import { ActivateStepLayout } from './activate-step/activate-step.layout';
import { SkillsStepLayout } from './skills-step/skills-step.layout';
import { DetailsStepLayout } from './details-step/details-step.layout';
import { useParams } from 'react-router';
import { Step } from '../../../components/general/stepper/step/step';
import { useStepperConnector } from '../../../components/general/stepper/use-stepper-connector';
import { StepIds } from './group-assessment.models';
import { IPerson } from '../assessment-management.models';
import { AssessmentRequest } from '../../../overmind/actions';

export function GroupAssessmentPage(): JSX.Element {
  const params = useParams();

  const { companyVariables } = useAppState();
  const actions = useAppActions();

  const [assessmentSlug, setAssessmentSlug] = useState<string | undefined>(params.assessmentSlug);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [details, setDetails] = useState<AssessmentRequest>();
  const [people, setPeople] = useState<IPerson[]>();
  const [roleSlug, setRoleSlug] = useState<string | undefined>();

  const stepperConnector = useStepperConnector();

  useEffect(() => {
    async function getDetails() {
      if (!assessmentSlug) return;
      const response = await actions.getAssessment(assessmentSlug);
      setDetails(
        !!response
          ? {
            ...response,
            minScore: response.minScore ?? 0,
            maxScore: response.maxScore ?? 0,
            assessmentSubType: response.assessmentSubType
          }
          : undefined
      );
    }
    async function getPeopleAndSkills() {
      if (!assessmentSlug) return;

      // Get all employees
      const assignees = await actions.getAssessmentAssignees(assessmentSlug!);

      // Try to get assessment assignees using the peer endorsement API
      const assessmentData = await actions.getPeerAssessment({ assessmentSlug: assessmentSlug });

      if (assessmentData?.assignees && assessmentData.assignees.length > 0) {
        // Process the peer endorsement data
        const peopleData: IPerson[] = [];

        // Get all skills for each person
        for (const peerAssignee of assessmentData.assignees) {
          const person = assignees?.assignees.find(assignee => assignee.learnerSlug === peerAssignee.learnerSlug);
          if (!person) continue;

          // Determine if this person is a manager by checking if they are marked as a manager by any other assessor
          const isManager = assessmentData.assignees.some(otherAssignee => otherAssignee.reviewers.some(reviewer => reviewer.learnerSlug === peerAssignee.learnerSlug && reviewer.isManager));

          // Get the skills for this person
          const skills: CompanySkillListItemResponse[] = [];

          // If there are skill IDs, fetch the skill details
          if (peerAssignee.companySkills && peerAssignee.companySkills.length > 0) {
            // For each skill ID, create a skill object
            // In a real implementation, we would fetch the skill details from the API
            // but for now, we'll create placeholder objects
            for (const companySkill of peerAssignee.companySkills) {
              skills.push({
                ...companySkill,
                description: '',
              });
            }
          }

          peopleData.push({
            ...person,
            selected: true,
            manager: isManager,
            skills: skills,
          });
        }

        setPeople(peopleData);
        return;
      }
    }

    async function getData() {
      if (!assessmentSlug) return;

      setIsLoading(true);

      await getDetails();
      await getPeopleAndSkills();

      setIsLoading(false);
    }

    getData();
  }, [assessmentSlug, companyVariables.slug]);

  return (
    <PageLoaderLayer path={PagePath.assessmentManagementCreateGroupAssessment}>
      <div className="wrapper stack" style={{ paddingBottom: 24, gap: 24 }}>
        <PageHeader
          breadcrumbs={
            !assessmentSlug
              ? [
                { name: 'Assessment Management', url: PagePath.assessmentManagementBase },
                { name: 'Create assessment', url: `${PagePath.assessmentManagementBase}${PagePath.assessmentManagementCreate.replace(':companySlug', companyVariables.slug!)}` },
                { name: details?.name ?? 'Group assessment', url: location.pathname },
              ]
              : [
                { name: 'Assessment Management', url: PagePath.assessmentManagementBase },
                { name: details?.name ?? 'Group assessment', url: location.pathname },
              ]
          }
          title={details?.name ?? 'Group assessment'}
          subtitle={!assessmentSlug ? 'Create and customise assessments by selecting skills, roles, and criteria for evaluation' : 'Edit and customise assessments by selecting skills, roles, and criteria for evaluation'}
          divider
        />

        <Stepper {...stepperConnector}>
          <Step key={StepIds.Details} label={StepIds.Details} completed={!!assessmentSlug} required>
            <DetailsStepLayout assessmentType={AssessmentType.PeerEndorsement} assessmentSlug={assessmentSlug} setAssessmentSlug={setAssessmentSlug} isLoading={isLoading} setIsLoading={setIsLoading} details={details} setDetails={setDetails} onNext={stepperConnector.next} />
          </Step>

          <Step key={StepIds.People} label={StepIds.People} completed={!!people?.length} required>
            <PeopleStepLayout assessmentSlug={assessmentSlug!} isLoading={isLoading} setIsLoading={setIsLoading} people={people} setPeople={setPeople} setRoleSlug={setRoleSlug} onBack={stepperConnector.previous} onNext={stepperConnector.next} />
          </Step>

          <Step key={StepIds.Skills} label={StepIds.Skills} completed={!!people?.length && people.every(person => !!person.skills.length)} required>
            <SkillsStepLayout assessmentSlug={assessmentSlug!} isLoading={isLoading} setIsLoading={setIsLoading} people={people} setPeople={setPeople} roleSlug={roleSlug} onBack={stepperConnector.previous} onNext={stepperConnector.next} />
          </Step>

          <Step key={StepIds.Activate} label={StepIds.Activate}>
            <ActivateStepLayout assessmentSlug={assessmentSlug!} isLoading={isLoading} setIsLoading={setIsLoading} onBack={stepperConnector.previous} />
          </Step>
        </Stepper>
      </div>
    </PageLoaderLayer>
  );
}
